import React from 'react';
import { GridColDef } from '@mui/x-data-grid';
import { formatDate } from '../../dashboard/components/dateUtils';
import { parseRawFormData } from '../../dashboard/components/formUtils';
import { HandlersRipResponse } from '../../app/athenaApiGen';
import {
  WorkflowProgressValue,
  getWorkflowProgress,
} from '../../utils/workflow';

// Extended type to handle rawFormData parsing
type RIPApiItem = HandlersRipResponse & {
  rawFormData?: string;
  workflowState?: string;
  workflowProgress?: WorkflowProgressValue;
};

export interface RIPRowData {
  id: string;
  ripNumber: string;
  ripDate: string | null;
  projectName: string;
  subject: string;
  contractorName: string;
  workflowState: string | null;
  workflowProgress: WorkflowProgressValue;
  workflowId: string | null;
}

const FALLBACK_TEXT = '--';

/**
 * Returns the first non-empty string value provided.
 */
const pickFirstString = (
  ...values: Array<string | null | undefined>
): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value;
  }
  return undefined;
};

export const createRIPColumns = (): GridColDef<RIPRowData>[] => [
  { field: 'ripNumber', headerName: 'RIP #', minWidth: 140, flex: 0.6 },
  {
    field: 'ripDate',
    headerName: 'RIP Date',
    minWidth: 140,
    renderCell: (params) => formatDate(params?.value),
    sortable: false,
    flex: 0.5,
  },
  { field: 'projectName', headerName: 'Project', minWidth: 220, flex: 1 },
  { field: 'subject', headerName: 'Subject', minWidth: 200, flex: 1 },
  { field: 'contractorName', headerName: 'Contractor', minWidth: 180, flex: 0.8 },
  {
    field: 'workflowState',
    headerName: 'Status',
    minWidth: 200,
    flex: 0.9,
    sortable: false,
    valueGetter: (value: string) => value ?? '-',
  },
];

export const mapRIPApiDataToRows = (items: RIPApiItem[]): RIPRowData[] => {
  return items.map((it) => {
    const raw = parseRawFormData(it.rawFormData);
    const workflowState =
      pickFirstString(
        it.workflowState as string | undefined,
        raw?.workflowState,
        raw?.workflow?.state,
        raw?.workflowStateLabel,
      ) || null;
    const workflowProgress =
      (it.workflowProgress as WorkflowProgressValue | undefined) ??
      (raw?.workflowProgress as WorkflowProgressValue | undefined) ??
      getWorkflowProgress(workflowState ?? undefined);
    return {
      id: it.ripId ?? '',
      ripNumber: pickFirstString(it.ripNumber, raw?.ripNumber) ?? FALLBACK_TEXT,
      ripDate: pickFirstString(it.ripDate, raw?.ripDate) || null,
      projectName: pickFirstString(it.projectName, raw?.projectName) ?? FALLBACK_TEXT,
      subject: pickFirstString(it.subject, raw?.subject) ?? FALLBACK_TEXT,
      contractorName: pickFirstString(it.contractorName, raw?.contractorName) ?? FALLBACK_TEXT,
      workflowState,
      workflowProgress,
      workflowId: it.workflowId ?? null,
    };
  });
};
